const STORAGE_KEY = 'earth_portal_tracking_params';

// Prefixes: any param starting with these is captured automatically
const TRACKING_PREFIXES = ['utm_'];

// Individual param names commonly used by ad platforms / marketing
const TRACKING_PARAMS = new Set([
  'i_cid',
  'gclid', // Google Ads
  'fbclid', // Facebook / Meta
  'msclkid', // Microsoft Ads
  'dclid', // DoubleClick
  'twclid', // Twitter / X
  'ttclid', // TikTok
  'li_fat_id', // LinkedIn
  'wbraid', // Google web-to-app
  'gbraid', // Google app-to-web
  'plan', // Marketing site pre-selected plan
  'billingCycle', // Marketing site pre-selected billing cycle
]);

const isTrackingParam = (key: string): boolean =>
  TRACKING_PREFIXES.some((p) => key.startsWith(p)) || TRACKING_PARAMS.has(key);

/**
 * Captures tracking params (utm_*, gclid, fbclid, etc.) from the current URL
 * into sessionStorage. Call once on app init, before React renders.
 */
export const captureTrackingParams = (
  search = window.location.search
): void => {
  try {
    const searchParams = new URLSearchParams(search);
    const tracking: Record<string, string> = {};

    searchParams.forEach((value, key) => {
      if (isTrackingParam(key)) {
        tracking[key] = value;
      }
    });

    if (Object.keys(tracking).length > 0) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(tracking));
    }
  } catch {
    // Never break app startup — silently skip if anything goes wrong
  }
};

/**
 * Returns stored tracking params as a key-value map, or empty object if none stored.
 */
export const getTrackingParams = (): Record<string, string> => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return {};
    const parsed: unknown = JSON.parse(stored);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed))
      return {};
    const result: Record<string, string> = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === 'string') {
        result[key] = value;
      }
    }
    return result;
  } catch {
    return {};
  }
};

/**
 * Removes specific keys from stored tracking params.
 * Use when a tracking param is no longer relevant (e.g. plan mismatch).
 */
export const removeTrackingParams = (...keys: string[]): void => {
  try {
    const keysToRemove = new Set(keys);
    const tracking = getTrackingParams();
    const filtered = Object.fromEntries(
      Object.entries(tracking).filter(([key]) => !keysToRemove.has(key))
    );
    if (Object.keys(filtered).length > 0) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Never break the app
  }
};

/**
 * Merges stored tracking params into a path, preserving any existing search params.
 * Example: mergeTrackingParams("business-type") → "business-type?utm_medium=x&i_cid=y"
 */
export const mergeTrackingParams = (to: string): string => {
  try {
    const tracking = getTrackingParams();
    if (Object.keys(tracking).length === 0) return to;

    // Separate hash fragment before parsing query string
    const hashIndex = to.indexOf('#');
    const hash = hashIndex >= 0 ? to.slice(hashIndex) : '';
    const withoutHash = hashIndex >= 0 ? to.slice(0, hashIndex) : to;

    const [path, existingSearch] = withoutHash.split('?');
    const params = new URLSearchParams(existingSearch || '');

    for (const [key, value] of Object.entries(tracking)) {
      // Don't overwrite if already present in the target URL
      if (!params.has(key)) {
        params.set(key, value);
      }
    }

    const search = params.toString();
    return search ? `${path}?${search}${hash}` : `${path}${hash}`;
  } catch {
    // Never break navigation — return the original path on any error
    return to;
  }
};
